import React, {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useRef,
  CSSProperties
} from 'react';
import { useLocation } from 'react-router';

import styles from './styles.module.css';
import { Button } from '../../components/Form/Button';
import SuccessIcon from '@/assets/icons/success-icon.svg?react';
import ErrorIcon from '@/assets/icons/error-toast.svg?react';
import InfoIcon from '@/assets/icons/confirm-delete.svg?react';

/**
 * Props for the ModalProvider component
 */
type ModalProviderProps = {
  /** React children to be wrapped by the modal provider */
  children: ReactNode;
};

/**
 * Modal context interface defining all available modal operations and state
 */
/**
 * Configuration options for modal behavior
 */
type ModalConfig = {
  /** Whether clicking outside the modal closes it */
  closeOnOverlayClick?: boolean;
  /** Whether pressing ESC closes the modal */
  closeOnEscape?: boolean;
  /** Custom CSS class or inline styles for the modal content */
  className?: string | CSSProperties;
  /** Whether to close modal on route change */
  closeOnRouteChange?: boolean;
  /** Modal text title */
  modalTitle?: string;
  /** Modal text content title */
  contentTitle?: string;
  /** Modal text content subtitle */
  contentSubtitle?: string;
  /** Modal text content description */
  contentDescription?: string | ReactNode;
  /** Modal icon (SVG import or React element) */
  modalIcon?: string | React.ComponentType;
  /** Array of buttons to be displayed in the modal */
  modalButtons?: Array<{
    label: string;
    onClick: () => void | Promise<void>;
    btnIcon?: string | React.ComponentType;
    variant: 'okBlue' | 'cancelBlue' | 'okRed' | 'cancelRed';
    disabled?: boolean;
    isLoader?: boolean;
  }>;
  /** Alignment of the buttons */
  alignButtons?: 'center' | 'left' | 'right';
  /** Auto close modal after specified time in milliseconds */
  autoClose?: number;
  variant?: 'technicalMessage';
};

export type TypedModalData = {
  type: 'success' | 'info' | 'error';
  title: string;
  message?: string;
  icon?: React.ComponentType;
  autoClose?: number;
};

type ModalContextType = {
  /** Function to close the modal and clear its content */
  closeModal: () => void;
  /** Function to open the modal (without changing content) */
  openModal: () => void;
  /** Function to toggle the modal state */
  toggleModal: () => void;
  /** Function to update modal content and open the modal with optional config */

  updateModalContent: (content: ReactNode, config?: ModalConfig) => void;
  /** Function to show a typed modal (success, info, error) */
  showTypedModal: (data: TypedModalData) => void;
  /** Current modal open/closed state */
  isOpen: boolean;
  /** Current content displayed in the modal */
  modalContent: ReactNode | null;
  /** Current modal configuration */
  modalConfig: ModalConfig;
};

/**
 * Default modal configuration
 */
const defaultConfig: ModalConfig = {
  closeOnOverlayClick: true,
  closeOnEscape: true,
  closeOnRouteChange: true,
  modalTitle: '',
  modalIcon: undefined,
  modalButtons: [],
  alignButtons: 'center',
  contentTitle: undefined,
  contentSubtitle: undefined,
  contentDescription: undefined
};

/**
 * Initial context value with no-op functions
 * This is used as the default value for the context
 */
const initialValue: ModalContextType = {
  closeModal: () => {},
  openModal: () => {},
  toggleModal: () => {},
  updateModalContent: (_content: ReactNode, _config?: ModalConfig) => {
    void _content;
    void _config;
  },
  showTypedModal: (_data: TypedModalData) => {
    void _data;
  },
  isOpen: false,
  modalContent: null,
  modalConfig: defaultConfig
};

/**
 * React context for modal state management
 */
const ModalContext = createContext<ModalContextType>(initialValue);

/**
 * Modal provider component that wraps the application and provides modal functionality
 *
 * @param props - ModalProviderProps containing children to wrap
 * @returns JSX element with modal context provider
 *
 * @example
 * ```tsx
 * <ModalProvider>
 *   <App />
 * </ModalProvider>
 * ```
 */
export const ModalProvider = ({ children = null }: ModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<null | ReactNode>(null);
  const [modalConfig, setModalConfig] = useState<ModalConfig>(defaultConfig);
  const [loadingButtons, setLoadingButtons] = useState<Set<string>>(new Set());
  const location = useLocation();

  /**
   * Closes the modal and clears its content
   */
  const closeModal = () => {
    setIsOpen(false);
    setModalContent(null);
    setLoadingButtons(new Set());
  };

  /**
   * Opens the modal without changing its content
   */
  const openModal = () => setIsOpen(true);

  /**
   * Toggles the modal open/closed state
   */
  const toggleModal = () => setIsOpen(prevState => !prevState);

  /**
   * Updates the modal content and opens the modal
   * @param content - React node to display in the modal
   * @param config - Optional configuration for modal behavior
   */
  const updateModalContent = (content: ReactNode, config?: ModalConfig) => {
    setModalContent(content);
    if (config) {
      setModalConfig({ ...defaultConfig, ...config });
    } else {
      setModalConfig(defaultConfig);
    }
    setIsOpen(true);
  };

  const showTypedModal = (data: TypedModalData) => {
    const getDefaultIcon = () => {
      switch (data.type) {
        case 'success':
          return SuccessIcon;
        case 'error':
          return ErrorIcon;
        case 'info':
          return InfoIcon;
        default:
          return SuccessIcon;
      }
    };

    const getDefaultAutoClose = () => {
      switch (data.type) {
        case 'success':
          return 3000;
        case 'error':
          return 5000;
        case 'info':
          return 3000;
        default:
          return 3000;
      }
    };

    const getStyleClass = () => {
      switch (data.type) {
        case 'success':
          return 'successModalTitle';
        case 'error':
          return 'errorModalTitle';
        case 'info':
          return 'successModalTitle';
        default:
          return 'successModalTitle';
      }
    };

    const IconComponent = data.icon || getDefaultIcon();
    const autoCloseTime = data.autoClose ?? getDefaultAutoClose();
    const titleClass = getStyleClass();

    const content = (
      <div className={styles.successModalContainer}>
        <div className={styles.successModalIcon}>
          <IconComponent />
        </div>
        <h2 className={styles[titleClass]}>{data.title}</h2>
        {data.message && (
          <p className={styles.successModalMessage}>{data.message}</p>
        )}
      </div>
    );

    updateModalContent(content, {
      modalTitle: '',
      modalButtons: [],
      autoClose: autoCloseTime
    });
  };

  /**
   * Handles button click with loading state management
   * @param buttonLabel - Label of the button to identify it
   * @param originalOnClick - Original onClick function
   * @param isLoader - Whether this button should show loader
   */
  const handleButtonClick = async (
    buttonLabel: string,
    originalOnClick: () => void | Promise<void>,
    isLoader?: boolean
  ) => {
    if (isLoader) {
      setLoadingButtons(prev => new Set(prev).add(buttonLabel));
    }

    try {
      await originalOnClick();
    } catch (error) {
      console.error('Error in button click handler:', error);
    } finally {
      if (isLoader) {
        setLoadingButtons(prev => {
          const newSet = new Set(prev);
          newSet.delete(buttonLabel);
          return newSet;
        });
      }
    }
  };

  /**
   * Maps modal button variants to Form/Button props
   * @param variant - Modal button variant
   * @returns Object with color and solid props for Form/Button
   */
  const mapButtonVariant = (
    variant: 'okBlue' | 'cancelBlue' | 'okRed' | 'cancelRed'
  ) => {
    switch (variant) {
      case 'okBlue':
        return { color: 'blue' as const, solid: true };
      case 'cancelBlue':
        return { color: 'blue' as const, solid: false };
      case 'okRed':
        return { color: 'red' as const, solid: true };
      case 'cancelRed':
        return { color: 'red' as const, solid: false };
      default:
        return { color: 'blue' as const, solid: true };
    }
  };

  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  /**
   * Auto close modal after specified time
   */
  useEffect(() => {
    if (isOpen && modalConfig.autoClose && modalConfig.autoClose > 0) {
      const timer = setTimeout(() => {
        closeModal();
      }, modalConfig.autoClose);

      return () => clearTimeout(timer);
    }
  }, [isOpen, modalConfig.autoClose]);

  /**
   * Close modal when route changes to prevent modal from staying open
   * Uses ref to avoid stale closure issues
   */
  useEffect(() => {
    if (isOpenRef.current && modalConfig.closeOnRouteChange) {
      closeModal();
    }
  }, [location, modalConfig.closeOnRouteChange]);

  /**
   * Add ESC key listener to close modal
   * Automatically removes listener when component unmounts
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && modalConfig.closeOnEscape) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, modalConfig.closeOnEscape]);

  const isTechnicalMessage = modalConfig.variant === 'technicalMessage';
  const contentStyles = [
    modalConfig.modalButtons && modalConfig.modalButtons.length > 0
      ? styles.modalContent
      : styles.modalContentNoButtons,
    isTechnicalMessage ? styles.technicalMessageModal : ''
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <ModalContext.Provider
      value={{
        closeModal,
        openModal,
        toggleModal,
        updateModalContent,
        showTypedModal,
        isOpen,
        modalContent,
        modalConfig
      }}
    >
      {/* Modal overlay and content - only rendered when modal is open */}
      {isOpen && (
        <div
          className={styles.modalOverlay}
          onClick={modalConfig.closeOnOverlayClick ? closeModal : undefined}
        >
          <div
            className={
              typeof modalConfig.className === 'string'
                ? `${contentStyles} ${modalConfig.className || ''}`
                : contentStyles
            }
            style={
              typeof modalConfig.className === 'object'
                ? modalConfig.className
                : undefined
            }
            onClick={e => e.stopPropagation()}
          >
            {(modalConfig.modalTitle || modalConfig.modalIcon) && (
              <div
                className={`${styles.modalTitle} ${isTechnicalMessage ? styles.technicalMessageTitle : ''}`}
              >
                {modalConfig.modalIcon &&
                  (typeof modalConfig.modalIcon === 'string' ? (
                    <img
                      src={modalConfig.modalIcon}
                      className={`${styles.modalIcon} ${isTechnicalMessage ? styles.technicalMessageIcon : ''}`}
                    />
                  ) : (
                    <div
                      className={`${styles.modalIcon} ${isTechnicalMessage ? styles.technicalMessageIcon : ''}`}
                    >
                      <modalConfig.modalIcon />
                    </div>
                  ))}
                {modalConfig.modalTitle && (
                  <span>{modalConfig.modalTitle}</span>
                )}
              </div>
            )}

            {modalConfig.contentTitle ||
            modalConfig.contentSubtitle ||
            modalConfig.contentDescription ? (
              <div
                className={`${styles.contentContainer} ${isTechnicalMessage ? styles.technicalMessageContent : ''}`}
              >
                {modalConfig.contentTitle && (
                  <div className={styles.contentTitle}>
                    {modalConfig.contentTitle}
                  </div>
                )}
                {modalConfig.contentSubtitle && (
                  <div className={styles.contentSubtitle}>
                    {modalConfig.contentSubtitle}
                  </div>
                )}
                {modalConfig.contentDescription && (
                  <div
                    className={`${styles.contentDescription} ${isTechnicalMessage ? styles.technicalMessageDescription : ''}`}
                  >
                    {modalConfig.contentDescription}
                  </div>
                )}
              </div>
            ) : null}

            {!modalConfig.contentTitle &&
              !modalConfig.contentSubtitle &&
              !modalConfig.contentDescription &&
              modalContent}
            {modalConfig.modalButtons &&
              modalConfig.modalButtons.length > 0 && (
                <div
                  className={`${styles.modalButtons} ${isTechnicalMessage ? styles.technicalMessageButtons : ''}`}
                  style={{
                    justifyContent:
                      modalConfig.alignButtons === 'left'
                        ? 'flex-start'
                        : modalConfig.alignButtons === 'right'
                          ? 'flex-end'
                          : 'center'
                  }}
                >
                  {modalConfig.modalButtons &&
                    modalConfig.modalButtons.length > 0 &&
                    modalConfig.modalButtons.map(button => {
                      const buttonProps = mapButtonVariant(button.variant);
                      const isLoading = loadingButtons.has(button.label);
                      const isDisabled = button.disabled || isLoading;

                      const iconComponent =
                        typeof button.btnIcon === 'string'
                          ? undefined
                          : button.btnIcon;

                      return (
                        <Button
                          key={button.label}
                          label={button.label}
                          onClick={() => {
                            if (!isLoading) {
                              handleButtonClick(
                                button.label,
                                button.onClick,
                                button.isLoader
                              );
                            }
                          }}
                          disabled={isDisabled && !isLoading}
                          loading={isLoading}
                          color={buttonProps.color}
                          solid={buttonProps.solid}
                          icon={iconComponent}
                          size="large"
                        />
                      );
                    })}
                </div>
              )}
          </div>
        </div>
      )}
      {children}
    </ModalContext.Provider>
  );
};

export default ModalContext;
